import { useDataGrid } from 'components/devexpress/Grid/dx-grid';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import pageStyles from 'pages/authorized/warranty-request/WarrantyRequest.module.scss';
import { StringParam, useQueryParams } from 'react-router-url-params';
import { Loading } from 'components/uikit/suspense/Loading';
import clsx from 'clsx';
import { Breadcrumbs } from 'components/uikit/Breadcrumbs';
import {
  BasketPart,
  WarrantyRequestBasketClient,
  WarrantyRequestBasketQuery,
} from 'services/api/api-client';
import {
  pagingSortingQueryParams,
  pagingSortingToBackendRequest,
} from 'helpers/pagination-helper';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import {
  Button,
  ButtonColor,
} from '../../../components/uikit/buttons/Button.tsx';
import {
  SameDeviceInfo,
  WarrantyRequestModal,
} from './WarrantyRequestModal.tsx';
import { Tooltip } from '../../../components/uikit/Tooltip.tsx';
import { Trans, UseTranslationResponse } from 'react-i18next';
import { RemindModalWarrantyRequest } from './components/RemindModalWarrantyRequest.tsx';
import SubmitModal from './components/SubmitModal.tsx';
import { WarrantyRequestEditModal } from './WarrantyRequestEditModal.tsx';
import { SubmitLoading } from './components/SubmitLoading.tsx';
import { useQueryClient } from '@tanstack/react-query';
import { TablePlaceholder } from '../../../components/uikit/table/TablePlaceholder.tsx';
import { AppLink } from '../../../components/uikit/buttons/AppLink.tsx';
import { Links } from '../../../application/constants/links.ts';
import { AccordionHeader } from '../../../components/uikit/AccordionHeader.tsx';
import { AppPagination } from '../../../components/uikit/pagination/AppPagination.tsx';
import { Accordion } from './components/Accordion.tsx';
import { useAnalytics } from 'helpers/analytics.ts';
import { CustomModal } from '../../../components/uikit/modal/CustomModal.tsx';
import { abasUnavailableUrn, ErrObj } from '../../../helpers/abas-helper.ts';
import { AbasPlaceholder } from '../../../components/uikit/AbasPlaceholder.tsx';
import { DataGridRef } from 'devextreme-react/data-grid';

export const WarrantyRequestPageCategory = 'Warranty request page';

const pageSizes = [10, 50, 100];

type RemovePartModal = {
  visible: boolean;
  itemNumber: string;
};

export type AddPartModal = {
  visible: boolean;
  sameDeviceInfo?: SameDeviceInfo;
};

type EditPartModal = {
  visible: boolean;
  itemNumber: string;
  deviceName: string;
  serviceProductId: string;
  articleName: string;
};

export type LoadingType = 'default' | 'submit' | null;

const columnsOfOuterTable: {
  translateKey: Parameters<
    UseTranslationResponse<'translation', 'Page.WarrantyPage'>['t']
  >[0];
  serverKey: string;
  width: number | string;
  classname?: string;
  isSortingTurnedOff?: boolean;
}[] = [
  {
    serverKey: 'serprod',
    translateKey: 'table_SN',
    width: 200,
  },
  {
    serverKey: 'ersatzbez',
    translateKey: 'table_device_name',
    width: 500,
  },
  {
    serverKey: 'ersatzteil',
    translateKey: 'table_article',
    width: 'unset',
    classname: clsx(pageStyles.minWidth200, pageStyles.flex1),
  },
  {
    serverKey: 'tybausfther',
    translateKey: 'table_parts_count',
    width: 300,
    isSortingTurnedOff: true,
  },
];

export const WarrantyRequestPage = () => {
  const i18n = useScopedTranslation('Page.WarrantyPage');
  const queryClient = useQueryClient();
  const { trackEvent } = useAnalytics();

  const [addToCartModalInfo, setAddToCartModalInfo] = useState<AddPartModal>({
    visible: false,
    sameDeviceInfo: undefined,
  });
  const [removeFromCartModalInfo, setRemoveFromCartModalInfo] =
    useState<RemovePartModal>({
      visible: false,
      itemNumber: '',
    });

  const [isClearCartModalVisible, setIsClearCartModalVisible] = useState(false);

  const [editPartModalInfo, setEditPartModalInfo] = useState<EditPartModal>({
    visible: false,
    itemNumber: '',
    deviceName: '',
    articleName: '',
    serviceProductId: '',
  });

  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [pageLoadingType, setPageLoadingType] = useState<LoadingType>(null);
  const [isSubmitModalVisible, setIsSubmitModalVisible] = useState(false);
  const [requestNumber, setRequestNumber] = useState('');
  const [closedOrders, setClosedOrders] = useState<Set<string>>(new Set([]));
  const [hoveredHeader, setHoveredHeader] = useState<string | null>(null);

  useDataGrid();

  const [params, setParams] = useQueryParams({
    serialNumber: StringParam,
    ...pagingSortingQueryParams(pageSizes[0]),
  });

  const basketQuery = WarrantyRequestBasketQuery.useGetQuery(
    {
      ...pagingSortingToBackendRequest(params),
    },
    {
      throwOnError: false,
      staleTime: 30 * 1000,
      placeholderData: (previousData) => previousData,
    },
  );

  const data = basketQuery.data;
  const records = data?.records ?? [];

  const dataGridRef = useRef<DataGridRef<any, number>>(null);
  const tableInstance = dataGridRef.current?.instance;

  useEffect(() => {
    if (!tableInstance) {
      setAddToCartModalInfo((prevState) => ({ ...prevState }));
    }
  }, [tableInstance]);

  const changePagination = (pagination: {
    page?: number;
    perPage?: number;
  }) => {
    setParams((prevParams) => ({
      ...prevParams,
      ...(pagination.page !== undefined && { page: pagination.page }),
      ...(pagination.perPage !== undefined && { perPage: pagination.perPage }),
    }));

    openAllAccordions();
  };

  const countOfAllParts = useMemo(
    () => records.flatMap((x) => x.parts).length,
    [data],
  );

  const showAddToCartModal = () => {
    trackEvent({
      category: WarrantyRequestPageCategory,
      action: 'Open add to cart modal',
      name: `Total items in cart: ${countOfAllParts}`,
    });

    setAddToCartModalInfo({ visible: true });
  };

  const closeAddToCartModal = () => {
    trackEvent({
      category: WarrantyRequestPageCategory,
      action: 'Close add to cart modal',
      name: 'Close add to cart modal',
    });
    setAddToCartModalInfo({ visible: false });
  };

  const closeEditPartModal = () => {
    trackEvent({
      category: WarrantyRequestPageCategory,
      action: 'Close edit part modal',
      name: 'Close edit part modal',
    });
    setEditPartModalInfo({
      ...editPartModalInfo,
      visible: false,
    });
  };

  const handleAddToCartModalSubmit = async () => {
    closeAddToCartModal();
    const { data: freshBasket } = await basketQuery.refetch();

    if (addToCartModalInfo.sameDeviceInfo?.serviceProductId) {
      await queryClient.refetchQueries({
        queryKey: WarrantyRequestBasketQuery.searchLastAddedDeviceQueryKey(
          addToCartModalInfo.sameDeviceInfo?.serviceProductId,
        ),
      });
    }
    const updatedTotalItems = freshBasket?.countOfRecords ?? 0;

    trackEvent({
      category: WarrantyRequestPageCategory,
      action: 'New part added to cart',
      name: `Total items in cart: ${updatedTotalItems}`,
    });

    setIsTooltipVisible(true);
    setTimeout(() => {
      setIsTooltipVisible(false);
    }, 2500);
  };
  const resetSameDeviceInfo = () => {
    setAddToCartModalInfo({ ...addToCartModalInfo, sameDeviceInfo: undefined });
  };

  const showSubmitModal = () => {
    trackEvent({
      category: WarrantyRequestPageCategory,
      action: 'Open send warranty request modal',
      name: `Total items in cart: ${countOfAllParts}`,
    });
    setIsSubmitModalVisible(true);
  };

  const closeSubmitModal = () => {
    trackEvent({
      category: WarrantyRequestPageCategory,
      action: 'Close send warranty request modal',
      name: 'Close send warranty request modal',
    });
    setIsSubmitModalVisible(false);
  };

  const switchLoader = (value: LoadingType) => {
    setPageLoadingType(value);
  };

  const handleChangeRequestNumber = (requestNumber: string) => {
    setRequestNumber(requestNumber);
  };

  const showRemoveItemFromCartModal = (itemNumber: string) => {
    setRemoveFromCartModalInfo({
      visible: true,
      itemNumber,
    });
  };

  const hideRemoveItemFromCartModal = () => {
    setRemoveFromCartModalInfo({
      visible: false,
      itemNumber: '',
    });
  };

  const showClearCartModal = () => {
    setIsClearCartModalVisible(true);
  };

  const hideClearCartModal = () => {
    setIsClearCartModalVisible(false);
  };

  const changeItemRemoveStatus = async () => {
    try {
      switchLoader('default');
      hideRemoveItemFromCartModal();

      await WarrantyRequestBasketClient.patchOrderStatus({
        isStored: true,
        itemNumber: removeFromCartModalInfo.itemNumber,
      });

      await basketQuery.refetch();
    } finally {
      switchLoader(null);
    }
  };

  const showEditPartModal = (
    cellInfo: BasketPart,
    productId: string,
    deviceName: string,
  ) => {
    setEditPartModalInfo({
      visible: true,
      itemNumber: cellInfo.itemId,
      deviceName: deviceName,
      articleName: cellInfo.name,
      serviceProductId: productId,
    });
  };

  const turnOffSubmitLoader = () => {
    switchLoader(null);
  };

  const openAllAccordions = () => {
    setClosedOrders(new Set());
  };

  const closeAllAccordions = () => {
    setClosedOrders(new Set(records.map((x) => x.serialNumber ?? '')));
  };

  const clearCart = async () => {
    try {
      switchLoader('default');
      hideClearCartModal();

      await WarrantyRequestBasketClient.clearBasket();

      setParams((prevParams) => ({
        ...prevParams,
        page: 1,
      }));
      await queryClient.refetchQueries({
        queryKey: WarrantyRequestBasketQuery.getQueryKey({}),
        exact: false,
      });
    } finally {
      switchLoader(null);
    }
  };

  const paginationProps = useMemo(
    () => ({
      page: params.page,
      perPage: params.perPage,
      totalCount: data?.countOfRecords ?? 0,
      pageSizes: pageSizes,
      numberOfRows: countOfAllParts,
      changePagination: changePagination,
      customClassname: pageStyles.bottom104,
    }),
    [params, basketQuery?.data, pageSizes],
  );

  const changeSortTable = (key: string) => {
    const previousKey = params.sortBy;

    setParams((prevParams) => ({
      ...prevParams,
      sortBy: key,
      desc: previousKey === key ? !params.desc : false,
      page: 1,
    }));

    openAllAccordions();
  };

  const handleClickAccordion = (key: string) => {
    trackEvent({
      category: WarrantyRequestPageCategory,
      action: 'Click table row group',
      name: `Table group key: ${key}`,
    });

    const copy = new Set(closedOrders);

    if (copy.has(key)) {
      copy.delete(key);
    } else {
      copy.add(key);
    }

    setClosedOrders(copy);
  };

  const errObj = basketQuery?.error as ErrObj;
  const isExternalServiceUnavailable =
    errObj?.response?.type === abasUnavailableUrn;

  const isPlaceholderVisible =
    !countOfAllParts &&
    !basketQuery.isFetching &&
    pageLoadingType !== 'default' &&
    !isExternalServiceUnavailable;

  return (
    <div data-test-id={'create-warranty-request-container'}>
      {pageLoadingType === 'submit' ? (
        <SubmitLoading
          requestNumber={requestNumber}
          turnOffSubmitLoader={turnOffSubmitLoader}
        />
      ) : (
        <div>
          {editPartModalInfo.visible && (
            <WarrantyRequestEditModal
              title={i18n.t('edit_part')}
              handleModalClose={closeEditPartModal}
              serviceProductId={editPartModalInfo.serviceProductId}
              itemNumber={editPartModalInfo.itemNumber}
              deviceName={editPartModalInfo.deviceName}
              articleName={editPartModalInfo.articleName}
              isOpen={true}
              basketQuery={basketQuery}
            />
          )}
          {addToCartModalInfo.visible && (
            <WarrantyRequestModal
              title={i18n.t('add_new_part_to_cart')}
              handleModalClose={closeAddToCartModal}
              sameDeviceInfo={addToCartModalInfo.sameDeviceInfo}
              resetSameDeviceInfo={resetSameDeviceInfo}
              handleModalSubmit={handleAddToCartModalSubmit}
              isOpen={true}
            />
          )}
          <Breadcrumbs>
            <div className={pageStyles.rowContainer12}>
              <Button
                testId={'add-new-part-button'}
                color={ButtonColor.Secondary}
                title={i18n.t('button_add_new_part')}
                onClick={showAddToCartModal}
              />
              {records.length > 0 && (
                <Button
                  testId={'submit-warranty-request-button'}
                  color={ButtonColor.Primary}
                  title={i18n.t('button_submit_warranty_request')}
                  onClick={showSubmitModal}
                />
              )}
            </div>
          </Breadcrumbs>
          <Loading
            loading={basketQuery.isFetching || pageLoadingType === 'default'}
          >
            {!isExternalServiceUnavailable && (
              <>
                <AccordionHeader
                  desc={params.desc}
                  sortBy={params.sortBy ?? null}
                  i18n={i18n}
                  columnsOfOuterTable={columnsOfOuterTable}
                  hoveredHeader={hoveredHeader}
                  changeSortTable={changeSortTable}
                  setHoveredHeader={(value) => setHoveredHeader(value)}
                />
                <Accordion
                  closeAllAccordions={closeAllAccordions}
                  openAllAccordions={openAllAccordions}
                  records={records}
                  handleClickAccordion={handleClickAccordion}
                  closedOrders={closedOrders}
                  setAddToCartModalInfo={setAddToCartModalInfo}
                  countOfParts={
                    data?.records.flatMap((x) => x.parts).length ?? 0
                  }
                  showRemoveItemFromCartModal={showRemoveItemFromCartModal}
                  showEditPartModal={showEditPartModal}
                  showClearCartModal={showClearCartModal}
                />
              </>
            )}
            {isExternalServiceUnavailable && (
              <AbasPlaceholder containerClassname={pageStyles.marginTop152} />
            )}
            {isPlaceholderVisible && (
              <TablePlaceholder
                testId={'placeholder'}
                customContent={
                  <div className={pageStyles.placeholderContainer}>
                    <div
                      className={clsx(
                        pageStyles.title,
                        pageStyles.marginBottom12,
                      )}
                    >
                      {i18n.t('placeholder_title')}
                    </div>
                    <Trans
                      i18nKey={'Page.WarrantyPage.placeholder_text'}
                      components={{
                        a: (
                          <AppLink
                            to={Links.Authorized.WarrantyInfo.route}
                            className={pageStyles.underline}
                          />
                        ),
                      }}
                    />
                  </div>
                }
              />
            )}
            <AppPagination {...paginationProps} />
            <div className={clsx(pageStyles.greyText, pageStyles.linkToInfo)}>
              <Trans
                i18nKey={'Page.WarrantyPage.placeholder_text'}
                components={{
                  a: (
                    <AppLink
                      to={Links.Authorized.WarrantyInfo.route}
                      className={clsx(
                        pageStyles.underline,
                        pageStyles.greyText,
                      )}
                    />
                  ),
                }}
              />
            </div>
            {isTooltipVisible && !basketQuery.isFetching && (
              <Tooltip
                closeTooltip={() => setIsTooltipVisible(false)}
                bodyContent={<div>{i18n.t('tooltip_message')}</div>}
                className={pageStyles.tooltip}
                showCancelButton={true}
                testId={'part-added-notification'}
              />
            )}
          </Loading>
          {removeFromCartModalInfo.visible && (
            <CustomModal
              isOpen={true}
              onSubmit={changeItemRemoveStatus}
              onCancel={hideRemoveItemFromCartModal}
              cancelButtonText={i18n.t('cancel')}
              submitButtonText={i18n.t('remove_from_cart_modal_title')}
              titleText={i18n.t('remove_from_cart_modal_title')}
              bodyText={i18n.t('remove_from_cart_modal_body')}
              containerClassName={pageStyles.modalBodyMinWidth480}
            />
          )}
          {isClearCartModalVisible && (
            <CustomModal
              isOpen={true}
              onSubmit={clearCart}
              onCancel={hideClearCartModal}
              cancelButtonText={i18n.t('cancel')}
              submitButtonText={i18n.t('clear_cart')}
              titleText={i18n.t('clear_cart')}
              bodyText={i18n.t('clear_cart_modal_body')}
              submitButtonTestId={'confirm-clear-cart-button'}
              containerClassName={pageStyles.modalBodyMinWidth480}
            />
          )}
          {isSubmitModalVisible && (
            <SubmitModal
              submitButtonTestId={'send-warranty-request-button'}
              closeSubmitModal={closeSubmitModal}
              email={data?.mailStandard ?? ''}
              switchLoader={switchLoader}
              numberOfRows={data?.countOfRecords ?? 0}
              handleChangeRequestNumber={handleChangeRequestNumber}
              basketQuery={basketQuery}
            />
          )}
          <RemindModalWarrantyRequest
            isActive={records.length > 0}
            showSubmitModal={showSubmitModal}
          />
        </div>
      )}
    </div>
  );
};
